import { ReactNode } from 'react';

import { Box, Skeleton, Typography } from '@mui/material';

export const TOTALIZER_CARD_SX = {
  height: 116,
  minWidth: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column' as const,
  boxSizing: 'border-box' as const,
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
};

export type TotalizerAccent = 'primary' | 'info' | 'error';

export interface TotalizerCardShellProps {
  cardId: string;
  accent?: TotalizerAccent;
  children: ReactNode;
}

export function TotalizerCardShell({
  cardId,
  accent,
  children,
}: TotalizerCardShellProps) {
  return (
    <Box
      id={cardId}
      component="article"
      data-accent={accent ?? 'none'}
      sx={{
        ...TOTALIZER_CARD_SX,
        ...(accent
          ? {
              borderTop: '3px solid',
              borderTopColor: `${accent}.main`,
            }
          : {}),
      }}>
      {children}
    </Box>
  );
}

export interface TotalizerCardProps {
  totalizerId: string;
  ariaLabel: string;
  label: string;
  complement: string;
  isLoading: boolean;
  value: ReactNode;
  valueColor?: string;
  complementColor?: string;
  icon?: ReactNode;
  action?: ReactNode;
}

export function TotalizerCard({
  totalizerId,
  ariaLabel,
  label,
  complement,
  isLoading,
  value,
  valueColor = 'text.primary',
  complementColor = 'text.secondary',
  icon,
  action,
}: TotalizerCardProps) {
  return (
    <Box
      id={totalizerId}
      aria-label={ariaLabel}
      sx={{
        width: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        px: 2,
        py: 1.5,
        bgcolor: 'background.paper',
      }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={1}
        minHeight={16}>
        <Box
          display="flex"
          alignItems="center"
          gap={0.75}
          minWidth={0}
          sx={{ '& > svg': { fontSize: 18, flexShrink: 0 } }}>
          {icon}
          <Typography variant="caption" color="text.secondary" noWrap>
            {label}
          </Typography>
        </Box>
        {action}
      </Box>
      <Box
        flex={1}
        display="flex"
        alignItems="center"
        minHeight={32}
        minWidth={0}>
        {isLoading ? (
          <Skeleton
            variant="text"
            animation="wave"
            width="72%"
            height={32}
            aria-label={`Carregando ${label}`}
          />
        ) : (
          <Typography
            variant="h5"
            color={valueColor}
            noWrap
            sx={{ lineHeight: '26px' }}>
            {value}
          </Typography>
        )}
      </Box>
      <Typography
        variant="caption"
        color={complementColor}
        noWrap
        sx={{ minHeight: 16 }}>
        {isLoading ? (
          <Skeleton variant="text" animation="wave" width="88%" />
        ) : (
          complement
        )}
      </Typography>
    </Box>
  );
}
